import { apiClient, handleApiRequest } from './client'
import type {
  DirectorConfig,
  DirectorDecision,
  DirectorPendingItem,
  DirectorDecisionCreatePayload,
  DirectorHistoryItem,
  DirectorRequestDetail,
  HRDirectorDecisionRow,
  DirectorTimelineEvent,
  DirectorRemindPayload,
  DirectorRemindResponse,
} from '@/types/director'

export interface DirectorDelegationItem {
  id: string
  faculty_id: string | null
  faculty_name: string
  delegating_director_id: string
  delegating_director_name: string
  delegate_director_id: string
  delegate_director_name: string
  start_date: string | null
  end_date: string | null
  is_active: boolean
  is_currently_active: boolean
  created_at: string
}

export interface FacultyWithDirectors {
  id: string
  name: string
  code: string | null
  directors: { id: string; name: string; email: string; is_lead: boolean }[]
}

export interface DelegationCreatePayload {
  faculty_id?: string
  delegate_director_id: string
  delegating_director_id?: string
  start_date?: string
  end_date?: string
  notes?: string
}

export interface SchoolDirectorItem {
  id: string
  name: string
  email: string
}

export interface AssignmentCreatePayload {
  faculty_id: string
  director_id: string
  is_lead?: boolean
}

export interface AssignmentResponse {
  id: string
  faculty_id: string
  faculty_name: string
  director_id: string
  director_name: string
  is_lead: boolean
  assigned_at: string
}

export const directorApi = {
  getPending: () =>
    handleApiRequest<DirectorPendingItem[]>(apiClient.get('/api/v1/director/pending')),

  abortSearch: (requestId: string, reason?: string) =>
    handleApiRequest<{ ok: boolean }>(
      apiClient.post(`/api/v1/director/requests/${requestId}/abort-search`, { reason }),
    ),

  releaseLesson: (requestId: string, lessonId: string) =>
    handleApiRequest<{ ok: boolean; status: string }>(
      apiClient.post(`/api/v1/director/requests/${requestId}/release-lesson`, { lesson_id: lessonId }),
    ),

  markNoticeRead: (noticeId: string) =>
    handleApiRequest<{ ok: boolean }>(
      apiClient.post(`/api/v1/director/notices/${noticeId}/read`),
    ),

  setRetroPayer: (retroId: string, paymentType: 'private' | 'school') =>
    handleApiRequest<{ ok: boolean; payment_type: string }>(
      apiClient.patch(`/api/v1/director/retro/${retroId}/payer`, { payment_type: paymentType }),
    ),

  requestRetroDocuments: (retroId: string) =>
    handleApiRequest<{ ok: boolean }>(
      apiClient.post(`/api/v1/director/retro/${retroId}/request-documents`),
    ),

  createDecision: (data: DirectorDecisionCreatePayload) =>
    handleApiRequest<DirectorDecision>(apiClient.post('/api/v1/director/decisions', data)),

  patchDecision: (decisionId: string, data: { payment_type?: 'private' | 'school'; rejection_note?: string }) =>
    handleApiRequest<DirectorDecision>(apiClient.patch(`/api/v1/director/decisions/${decisionId}`, data)),

  requestDocuments: (decisionId: string, data: { document_requests: string[]; custom_document_request?: string }) =>
    handleApiRequest<DirectorDecision>(apiClient.post(`/api/v1/director/decisions/${decisionId}/documents`, data)),

  getHistory: () =>
    handleApiRequest<DirectorHistoryItem[]>(apiClient.get('/api/v1/director/history')),

  getConfig: () =>
    handleApiRequest<DirectorConfig>(apiClient.get('/api/v1/director/config')),

  getHRDecisions: () =>
    handleApiRequest<HRDirectorDecisionRow[]>(apiClient.get('/api/v1/director/hr/decisions')),

  // Faculty delegations
  getDelegations: () =>
    handleApiRequest<DirectorDelegationItem[]>(apiClient.get('/api/v1/director/delegations')),

  createDelegation: (data: DelegationCreatePayload) =>
    handleApiRequest<DirectorDelegationItem>(apiClient.post('/api/v1/director/delegations', data)),

  revokeDelegation: (delegationId: string) =>
    handleApiRequest<void>(apiClient.delete(`/api/v1/director/delegations/${delegationId}`)),

  getFacultiesWithDirectors: () =>
    handleApiRequest<FacultyWithDirectors[]>(apiClient.get('/api/v1/director/faculties-with-directors')),

  getSchoolDirectors: () =>
    handleApiRequest<SchoolDirectorItem[]>(apiClient.get('/api/v1/director/school-directors')),

  // Faculty assignments
  createAssignment: (data: AssignmentCreatePayload) =>
    handleApiRequest<AssignmentResponse>(apiClient.post('/api/v1/director/faculty-assignments', data)),

  deleteAssignment: (assignmentId: string) =>
    handleApiRequest<void>(apiClient.delete(`/api/v1/director/faculty-assignments/${assignmentId}`)),

  // V2: Full-Detail, manuelle Zuweisung, Zahler-Patch
  getRequestDetail: (requestId: string) =>
    handleApiRequest<DirectorRequestDetail>(apiClient.get(`/api/v1/director/requests/${requestId}`)),

  // E8: Dokumente anfordern (Button im Director-Detail) — anfragengebunden
  requestRequestDocuments: (requestId: string) =>
    handleApiRequest<{ success: boolean; message: string }>(
      apiClient.post(`/api/v1/director/requests/${requestId}/request-documents`)
    ),

  cancelRequestDocuments: (requestId: string) =>
    handleApiRequest<{ success: boolean; message: string }>(
      apiClient.delete(`/api/v1/director/requests/${requestId}/request-documents`)
    ),

  // Fallakte v2 (P3/E4): volles Audit eines Falls
  getTimeline: (requestId: string) =>
    handleApiRequest<DirectorTimelineEvent[]>(
      apiClient.get(`/api/v1/director/requests/${requestId}/timeline`)
    ),

  // Fallakte v2 (P2/E3): Erinnerung an die Lehrperson
  remind: (requestId: string, data: DirectorRemindPayload) =>
    handleApiRequest<DirectorRemindResponse>(
      apiClient.post(`/api/v1/director/requests/${requestId}/remind`, data)
    ),
}

// HR-v2: hrFinanceApi (V2-Entscheidungs-Queue) wurde durch hrApi in
// services/api/hr.ts ersetzt (plans/hr-v2.md Phase 8.3, D2).

export interface DirectorRights {
  director_id: string
  school_id: string
  can_approve: boolean | null
  can_reject: boolean | null
  can_info_request: boolean | null
  can_request_documents: boolean | null
  can_decide_payer: boolean | null
  can_manual_assign: boolean | null
  can_adjust_case: boolean | null
  can_delegate: boolean | null
  visibility_scope: string | null
  see_dashboards: boolean | null
  threshold_days_override: number | null
  lesson_account_limit_override: number | null
}

export const superadminDirectorRightsApi = {
  getRights: (directorId: string) =>
    handleApiRequest<DirectorRights>(apiClient.get(`/api/v1/superadmin/directors/${directorId}/rights`)),

  updateRights: (directorId: string, data: Partial<DirectorRights>) =>
    handleApiRequest<DirectorRights>(apiClient.put(`/api/v1/superadmin/directors/${directorId}/rights`, data)),
}

export const superadminDirectorApi = {
  getActiveCount: () =>
    handleApiRequest<{ active_directors_count: number }>(apiClient.get('/api/v1/superadmin/director/active-count')),
}

// V5/C.3: Fach-Fakultät-Zuordnung (Routing-Basis 'Lektion', Kap. 10/E7)
export interface SubjectFacultyLink {
  id: string
  subject_id: string
  subject_name: string
  faculty_id: string
  faculty_name: string
  is_primary: boolean
}

export const subjectFacultyApi = {
  list: () =>
    handleApiRequest<SubjectFacultyLink[]>(apiClient.get('/api/v1/subject-faculties')),

  create: (data: { subject_id: string; faculty_id: string; is_primary?: boolean }) =>
    handleApiRequest<SubjectFacultyLink>(apiClient.post('/api/v1/subject-faculties', data)),

  remove: (linkId: string) =>
    handleApiRequest<void>(apiClient.delete(`/api/v1/subject-faculties/${linkId}`)),
}
